import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { CheckCircle, AlertCircle, Download, Info, X, ChevronDown } from 'lucide-react'
import DropZone from '../components/DropZone'
import { useJobs } from '../hooks/useJobs'
import { InfoBox } from '../components/UI'

const API_URL = process.env.REACT_APP_API_URL ?? 'https://vat-recovery.finkraftai.com'

// ── Rate data ─────────────────────────────────────────────────────────────────
const RATE_TABLE = [
  { iso: 'SG', country: 'Singapore',    standard: 9,    reduced: 9,    type: 'Domestic',     note: 'Same rate — no separate accommodation rate' },
  { iso: 'AU', country: 'Australia',    standard: 10,   reduced: 10,   type: 'Domestic',     note: 'Same rate' },
  { iso: 'NZ', country: 'New Zealand',  standard: 15,   reduced: 15,   type: 'Domestic',     note: 'Same rate' },
  { iso: 'JP', country: 'Japan',        standard: 10,   reduced: 10,   type: 'Domestic',     note: 'Same rate' },
  { iso: 'NO', country: 'Norway',       standard: 25,   reduced: 12,   type: 'Foreign Non-EU', note: 'Norwegian VAT Act §5-5 — low rate for accommodation (hotellovernatting)' },
  { iso: 'FI', country: 'Finland',      standard: 25.5, reduced: 14,   type: 'Foreign Non-EU', note: 'Finnish VAT Act §85a — reduced rate for accommodation services' },
  { iso: 'SE', country: 'Sweden',       standard: 25,   reduced: 12,   type: 'Foreign Non-EU', note: 'Swedish VAT Act Ch.7 §1 — 12% for hotel and camping accommodation' },
  { iso: 'GB', country: 'United Kingdom', standard: 20, reduced: 20,   type: 'Foreign Non-EU', note: 'Same rate — UK applies standard 20% to hotel accommodation' },
  { iso: 'NL', country: 'Netherlands',  standard: 21,   reduced: 9,    type: 'Foreign Non-EU', note: 'Dutch VAT Act Table I — 9% reduced rate for hotel accommodation' },
  { iso: 'IS', country: 'Iceland',      standard: 24,   reduced: 11,   type: 'Foreign Non-EU', note: 'Icelandic VAT Act — 11% reduced rate for accommodation' },
  { iso: 'KR', country: 'South Korea',  standard: 10,   reduced: 10,   type: 'Foreign Non-EU', note: 'Same rate' },
  { iso: 'JE', country: 'Jersey',       standard: 5,    reduced: 5,    type: 'Foreign Non-EU', note: 'Same rate' },
  { iso: 'LB', country: 'Lebanon',      standard: 11,   reduced: 11,   type: 'Foreign Non-EU', note: 'Same rate' },
]

// Countries where standard ≠ reduced (the ones that matter)
const DIFFERS = RATE_TABLE.filter(r => r.standard !== r.reduced)

// ── Rate mode options ─────────────────────────────────────────────────────────
const RATE_OPTIONS = [
  {
    value: 'reduced' as const,
    label: 'Reduced Hotel Rate',
    sublabel: 'Recommended · Audit-ready',
    desc: 'Uses the lower accommodation-specific VAT rate where applicable (e.g. Norway 12% not 25%). This is what tax authorities expect — confirmed against published VAT legislation to the cent.',
    color: '#15803d',
    bg: '#f0fdf4',
    border: '#86efac',
    badge: 'RECOMMENDED',
    badgeBg: '#dcfce7',
    badgeColor: '#15803d',
  },
  {
    value: 'standard' as const,
    label: 'Standard VAT Rate',
    sublabel: 'Higher estimate · May be rejected',
    desc: 'Uses the headline standard VAT rate from the chart (e.g. Norway 25% not 12%). Results in a higher VAT figure but tax authorities will reduce this to the correct accommodation rate. Use only for initial estimation.',
    color: '#b45309',
    bg: '#fffbeb',
    border: '#fcd34d',
    badge: 'OVER-ESTIMATE',
    badgeBg: '#fef3c7',
    badgeColor: '#b45309',
  },
]

// ── Reference modal ───────────────────────────────────────────────────────────
function RateReferenceModal({ mode, onClose }: { mode: 'reduced' | 'standard'; onClose: () => void }) {
  const isReduced = mode === 'reduced'
  return (
    <div style={{
      position: 'fixed', inset: 0, background: 'rgba(15,27,53,.55)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      zIndex: 1000, padding: 20,
    }} onClick={onClose}>
      <div style={{
        background: '#fff', borderRadius: 16, width: '100%', maxWidth: 720,
        maxHeight: '85vh', overflow: 'hidden', display: 'flex', flexDirection: 'column',
        boxShadow: '0 20px 60px rgba(0,0,0,.2)',
      }} onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div style={{
          padding: '18px 24px', borderBottom: '1px solid #e2e8f0',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          background: isReduced ? '#f0fdf4' : '#fffbeb',
        }}>
          <div>
            <p style={{ fontWeight: 700, fontSize: 15, color: '#0f172a' }}>
              {isReduced ? '🟢 Reduced Hotel Rate Reference' : '🟡 Standard VAT Rate Reference'}
            </p>
            <p style={{ fontSize: 12.5, color: '#475569', marginTop: 3 }}>
              {isReduced
                ? 'Accommodation-specific reduced rates per each country\'s VAT legislation · Audit-ready'
                : 'Standard headline VAT rates from VAT Chart 2025 Non-EU Claimants (India)'}
            </p>
          </div>
          <button onClick={onClose} style={{
            background: 'none', border: 'none', cursor: 'pointer',
            color: '#94a3b8', padding: 4, borderRadius: 6,
          }}>
            <X size={18} />
          </button>
        </div>

        {/* Body */}
        <div style={{ overflowY: 'auto', flex: 1 }}>
          {/* Intro box */}
          {isReduced ? (
            <div style={{ margin: '16px 24px 0', padding: '12px 16px', background: '#eff6ff', borderRadius: 8, border: '1px solid #bfdbfe', fontSize: 12.5, color: '#1d4ed8' }}>
              <strong>Why reduced rates?</strong> Most countries apply a lower VAT rate specifically to accommodation (hotels, lodging) as a tourism incentive. Using the wrong rate will cause the tax authority to reject or reduce your claim. These rates are confirmed against each country's published VAT legislation — verified to the cent.
            </div>
          ) : (
            <div style={{ margin: '16px 24px 0', padding: '12px 16px', background: '#fffbeb', borderRadius: 8, border: '1px solid #fde68a', fontSize: 12.5, color: '#92400e' }}>
              <strong>Caution:</strong> Standard rates overstate the claim for Norway, Finland, Sweden, Netherlands, and Iceland. Tax authorities in these countries will reduce the refund to the correct accommodation rate. Use this mode only for high-level estimation, not for actual submissions.
            </div>
          )}

          {/* Table */}
          <div style={{ padding: '16px 24px 24px' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13.5 }}>
              <thead>
                <tr style={{ background: '#f8fafc' }}>
                  <th style={{ padding: '10px 12px', textAlign: 'left', fontSize: 11.5, fontWeight: 600, color: '#475569', textTransform: 'uppercase', letterSpacing: '.5px', borderBottom: '1px solid #e2e8f0' }}>Country</th>
                  <th style={{ padding: '10px 12px', textAlign: 'center', fontSize: 11.5, fontWeight: 600, color: '#475569', textTransform: 'uppercase', letterSpacing: '.5px', borderBottom: '1px solid #e2e8f0' }}>ISO</th>
                  <th style={{ padding: '10px 12px', textAlign: 'center', fontSize: 11.5, fontWeight: 600, color: '#475569', textTransform: 'uppercase', letterSpacing: '.5px', borderBottom: '1px solid #e2e8f0' }}>Standard Rate</th>
                  <th style={{ padding: '10px 12px', textAlign: 'center', fontSize: 11.5, fontWeight: 600, color: isReduced ? '#15803d' : '#b45309', textTransform: 'uppercase', letterSpacing: '.5px', borderBottom: '1px solid #e2e8f0', background: isReduced ? '#f0fdf4' : '#fffbeb' }}>
                    {isReduced ? 'Hotel Rate (Used)' : 'Rate Used'}
                  </th>
                  <th style={{ padding: '10px 12px', textAlign: 'left', fontSize: 11.5, fontWeight: 600, color: '#475569', textTransform: 'uppercase', letterSpacing: '.5px', borderBottom: '1px solid #e2e8f0' }}>Source / Note</th>
                </tr>
              </thead>
              <tbody>
                {RATE_TABLE.map((r, i) => {
                  const differs = r.standard !== r.reduced
                  const displayRate = isReduced ? r.reduced : r.standard
                  const rowBg = differs ? (isReduced ? '#f0fdf4' : '#fffbeb') : (i % 2 === 0 ? '#ffffff' : '#f8fafc')
                  return (
                    <tr key={r.iso} style={{ background: rowBg }}>
                      <td style={{ padding: '10px 12px', fontWeight: 600, color: '#0f172a', borderBottom: '1px solid #f1f5f9' }}>
                        {r.country}
                        {differs && (
                          <span style={{ marginLeft: 6, fontSize: 10, fontWeight: 700, padding: '1px 6px', borderRadius: 4, background: isReduced ? '#dcfce7' : '#fef3c7', color: isReduced ? '#15803d' : '#b45309' }}>
                            {isReduced ? 'REDUCED' : 'HIGHER'}
                          </span>
                        )}
                      </td>
                      <td style={{ padding: '10px 12px', textAlign: 'center', fontFamily: 'JetBrains Mono', fontSize: 12, color: '#94a3b8', borderBottom: '1px solid #f1f5f9' }}>{r.iso}</td>
                      <td style={{ padding: '10px 12px', textAlign: 'center', fontFamily: 'JetBrains Mono', fontWeight: 600, color: differs ? '#b91c1c' : '#475569', textDecoration: differs && isReduced ? 'line-through' : 'none', borderBottom: '1px solid #f1f5f9' }}>
                        {r.standard}%
                      </td>
                      <td style={{ padding: '10px 12px', textAlign: 'center', fontFamily: 'JetBrains Mono', fontWeight: 700, color: differs ? (isReduced ? '#15803d' : '#b45309') : '#475569', fontSize: 14, borderBottom: '1px solid #f1f5f9', background: differs ? (isReduced ? '#dcfce7' : '#fef3c7') : 'transparent' }}>
                        {displayRate}%
                      </td>
                      <td style={{ padding: '10px 12px', fontSize: 12, color: '#64748b', borderBottom: '1px solid #f1f5f9' }}>{r.note}</td>
                    </tr>
                  )
                })}
              </tbody>
            </table>

            {/* Impact summary for countries that differ */}
            {isReduced && (
              <div style={{ marginTop: 20, background: '#f8fafc', borderRadius: 10, padding: '14px 16px', border: '1px solid #e2e8f0' }}>
                <p style={{ fontSize: 12.5, fontWeight: 600, color: '#0f172a', marginBottom: 10 }}>
                  Impact on countries with different rates (based on Hotel_info_India data):
                </p>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12.5 }}>
                  <thead>
                    <tr>
                      <th style={{ textAlign: 'left', padding: '6px 8px', color: '#94a3b8', fontSize: 11, textTransform: 'uppercase', letterSpacing: '.5px' }}>Country</th>
                      <th style={{ textAlign: 'right', padding: '6px 8px', color: '#94a3b8', fontSize: 11, textTransform: 'uppercase', letterSpacing: '.5px' }}>At Standard Rate</th>
                      <th style={{ textAlign: 'right', padding: '6px 8px', color: '#15803d', fontSize: 11, textTransform: 'uppercase', letterSpacing: '.5px' }}>At Reduced Rate</th>
                      <th style={{ textAlign: 'right', padding: '6px 8px', color: '#b91c1c', fontSize: 11, textTransform: 'uppercase', letterSpacing: '.5px' }}>Over-claim</th>
                    </tr>
                  </thead>
                  <tbody>
                    {[
                      { country: 'Norway',      std: 35636, red: 19091, diff: 16545 },
                      { country: 'Finland',     std: 30536, red: 18456, diff: 12080 },
                      { country: 'Sweden',      std: 21791, red: 11674, diff: 10117 },
                      { country: 'Netherlands', std: 4962,  red: 2361,  diff: 2601  },
                    ].map(r => (
                      <tr key={r.country}>
                        <td style={{ padding: '7px 8px', fontWeight: 500 }}>{r.country}</td>
                        <td style={{ padding: '7px 8px', textAlign: 'right', fontFamily: 'JetBrains Mono', color: '#b91c1c', fontSize: 12 }}>€{r.std.toLocaleString()}</td>
                        <td style={{ padding: '7px 8px', textAlign: 'right', fontFamily: 'JetBrains Mono', color: '#15803d', fontSize: 12, fontWeight: 700 }}>€{r.red.toLocaleString()}</td>
                        <td style={{ padding: '7px 8px', textAlign: 'right', fontFamily: 'JetBrains Mono', color: '#ef4444', fontSize: 12 }}>+€{r.diff.toLocaleString()}</td>
                      </tr>
                    ))}
                    <tr style={{ background: '#f1f5f9', fontWeight: 700 }}>
                      <td style={{ padding: '8px 8px' }}>Total</td>
                      <td style={{ padding: '8px 8px', textAlign: 'right', fontFamily: 'JetBrains Mono', color: '#b91c1c' }}>€92,924</td>
                      <td style={{ padding: '8px 8px', textAlign: 'right', fontFamily: 'JetBrains Mono', color: '#15803d' }}>€51,581</td>
                      <td style={{ padding: '8px 8px', textAlign: 'right', fontFamily: 'JetBrains Mono', color: '#ef4444' }}>+€41,343</td>
                    </tr>
                  </tbody>
                </table>
                <p style={{ fontSize: 11.5, color: '#94a3b8', marginTop: 10, fontStyle: 'italic' }}>
                  Based on Hotel_info_India__4_.xlsx · 2025 transactions · Reduced accommodation rates
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

// ── Upload page ───────────────────────────────────────────────────────────────
export default function Upload() {
  const navigate = useNavigate()
  const { upload } = useJobs()

  const [rateMode, setRateMode]     = useState<'reduced' | 'standard'>('reduced')
  const [showModal, setShowModal]   = useState<'reduced' | 'standard' | null>(null)
  const [showDropdown, setShowDropdown] = useState(false)

  const handleFiles = async (files: File[]) => {
    await upload.mutateAsync({ files, rateMode })
    navigate('/queue')
  }

  const selectedOption = RATE_OPTIONS.find(o => o.value === rateMode)!

  return (
    <div style={{ width: "100%" }}>
      {/* Modal */}
      {showModal && <RateReferenceModal mode={showModal} onClose={() => setShowModal(null)} />}

      {/* Page header */}
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 26 }}>
        <div>
          <h1 style={{ fontSize: 22, fontWeight: 700, color: '#0f172a', marginBottom: 4 }}>New VAT Analysis</h1>
          <p style={{ fontSize: 13.5, color: '#475569' }}>
            Upload expense files — multiple files process simultaneously
          </p>
        </div>
        <a href={`${API_URL}/static/finkraft_sample_upload.xlsx`} download="finkraft_sample_upload.xlsx" style={{ textDecoration: 'none' }}>
          <button style={{
            display: 'flex', alignItems: 'center', gap: 8,
            padding: '9px 16px', borderRadius: 9, background: '#0f1b35',
            color: '#fff', border: 'none', cursor: 'pointer',
            fontFamily: 'inherit', fontSize: 13, fontWeight: 600,
          }}>
            <Download size={13} /> Download Sample
          </button>
        </a>
      </div>

      {/* ── VAT Rate Mode Selector ── */}
      <div style={{
        background: '#fff', border: '1px solid #e2e8f0', borderRadius: 14,
        padding: '20px 22px', marginBottom: 22,
        boxShadow: '0 1px 3px rgba(0,0,0,.05)',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14 }}>
          <div>
            <p style={{ fontWeight: 600, fontSize: 14, color: '#0f172a' }}>VAT Rate Mode</p>
            <p style={{ fontSize: 12.5, color: '#475569', marginTop: 2 }}>
              Choose which rate to apply when calculating VAT potential
            </p>
          </div>
          {/* View reference buttons */}
          <div style={{ display: 'flex', gap: 8 }}>
            <button onClick={() => setShowModal('reduced')} style={{
              display: 'flex', alignItems: 'center', gap: 6, padding: '6px 12px',
              borderRadius: 7, background: '#f0fdf4', color: '#15803d',
              border: '1px solid #86efac', cursor: 'pointer', fontFamily: 'inherit',
              fontSize: 12, fontWeight: 600,
            }}>
              <Info size={12} /> Reduced Rate Chart
            </button>
            <button onClick={() => setShowModal('standard')} style={{
              display: 'flex', alignItems: 'center', gap: 6, padding: '6px 12px',
              borderRadius: 7, background: '#fffbeb', color: '#b45309',
              border: '1px solid #fcd34d', cursor: 'pointer', fontFamily: 'inherit',
              fontSize: 12, fontWeight: 600,
            }}>
              <Info size={12} /> Standard Rate Chart
            </button>
          </div>
        </div>

        {/* Two option cards */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          {RATE_OPTIONS.map(opt => (
            <div
              key={opt.value}
              onClick={() => setRateMode(opt.value)}
              style={{
                border: `2px solid ${rateMode === opt.value ? opt.color : '#e2e8f0'}`,
                borderRadius: 10, padding: '14px 16px', cursor: 'pointer',
                background: rateMode === opt.value ? opt.bg : '#fafafa',
                transition: 'all .15s',
              }}
            >
              <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 6 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  {/* Radio circle */}
                  <div style={{
                    width: 18, height: 18, borderRadius: '50%', flexShrink: 0,
                    border: `2px solid ${rateMode === opt.value ? opt.color : '#cbd5e1'}`,
                    background: rateMode === opt.value ? opt.color : '#fff',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                  }}>
                    {rateMode === opt.value && (
                      <div style={{ width: 7, height: 7, borderRadius: '50%', background: '#fff' }} />
                    )}
                  </div>
                  <p style={{ fontWeight: 700, fontSize: 13.5, color: '#0f172a' }}>{opt.label}</p>
                </div>
                <span style={{
                  fontSize: 10, fontWeight: 700, padding: '2px 7px', borderRadius: 4,
                  background: opt.badgeBg, color: opt.badgeColor,
                }}>
                  {opt.badge}
                </span>
              </div>
              <p style={{ fontSize: 12, color: '#475569', lineHeight: 1.5, paddingLeft: 28 }}>
                {opt.desc}
              </p>
              {/* Show diff countries */}
              {opt.value === 'reduced' && (
                <div style={{ paddingLeft: 28, marginTop: 8, display: 'flex', flexWrap: 'wrap', gap: 5 }}>
                  {DIFFERS.map(r => (
                    <span key={r.iso} style={{
                      fontSize: 11, padding: '1px 7px', borderRadius: 4,
                      background: '#dcfce7', color: '#15803d', fontWeight: 600,
                    }}>
                      {r.iso}: {r.reduced}% <span style={{ opacity: .5, textDecoration: 'line-through' }}>{r.standard}%</span>
                    </span>
                  ))}
                </div>
              )}
              {opt.value === 'standard' && (
                <div style={{ paddingLeft: 28, marginTop: 8, display: 'flex', flexWrap: 'wrap', gap: 5 }}>
                  {DIFFERS.map(r => (
                    <span key={r.iso} style={{
                      fontSize: 11, padding: '1px 7px', borderRadius: 4,
                      background: '#fef3c7', color: '#b45309', fontWeight: 600,
                    }}>
                      {r.iso}: {r.standard}%
                    </span>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>

        {/* Active selection summary */}
        <div style={{
          marginTop: 14, padding: '10px 14px', borderRadius: 8,
          background: selectedOption.bg, border: `1px solid ${selectedOption.border}`,
          display: 'flex', alignItems: 'center', gap: 10, fontSize: 12.5,
          color: selectedOption.color,
        }}>
          <span style={{ fontWeight: 700 }}>✓ Selected:</span>
          {rateMode === 'reduced'
            ? 'Files will be processed using reduced accommodation rates (Norway 12%, Finland 14%, Sweden 12%, Netherlands 9%, Iceland 11%). Recommended for all VAT recovery claims.'
            : 'Files will be processed using standard VAT rates from the chart. Norway 25%, Finland 25.5%, Sweden 25% — results in a higher but likely over-stated figure.'}
        </div>
      </div>

      {/* Upload zone */}
      <DropZone onFiles={handleFiles} disabled={upload.isPending} />

      {upload.isError && (
        <div style={{
          marginTop: 14, display: 'flex', alignItems: 'center', gap: 8,
          padding: '10px 14px', background: '#fef2f2', border: '1px solid #fecaca',
          borderRadius: 8, color: '#b91c1c', fontSize: 13,
        }}>
          <AlertCircle size={14} />
          {(upload.error as Error)?.message ?? 'Upload failed. Please try again.'}
        </div>
      )}

      {upload.isSuccess && (
        <div style={{
          marginTop: 14, display: 'flex', alignItems: 'center', gap: 8,
          padding: '10px 14px', background: '#f0fdf4', border: '1px solid #bbf7d0',
          borderRadius: 8, color: '#15803d', fontSize: 13,
        }}>
          <CheckCircle size={14} />
          Files uploaded with <strong style={{ marginLeft: 3 }}>{rateMode} rates</strong> — redirecting to queue…
        </div>
      )}

      {/* Column spec */}
      <div className="card" style={{ marginTop: 24 }}>
        <div className="card-header">
          <div>
            <p className="section-title">Accepted Column Names</p>
            <p className="section-sub">Sprinter Data Transfer Spec · Case-insensitive matching</p>
          </div>
          <span style={{ fontSize: 11, fontWeight: 600, padding: '3px 10px', borderRadius: 6, background: '#eff6ff', color: '#1d4ed8', border: '1px solid #bfdbfe' }}>
            VAT_Potential_Data_Fields_2026
          </span>
        </div>
        <div style={{ overflowX: 'auto' }}>
          <table className="data-table">
            <thead><tr>
              <th style={{ width: 36 }}>#</th>
              <th>Field Name</th>
              <th style={{ width: 120, textAlign: 'center' }}>Required?</th>
              <th>Example</th>
              <th>Notes</th>
            </tr></thead>
            <tbody>
              {[
                ['1',  'Client Identifier',       'mandatory',   '1234, IN01',                    'Unique entity/account ID'],
                ['2',  'Client Name',             'optional',    'XYZ Ltd.',                      'Company name'],
                ['3',  'Client Contact Details',  'optional',    'Address, phone',                'Can span multiple fields'],
                ['4',  'Destination Country',     'mandatory',   'US, SG, GB, NO',                '2-char ISO — where expense was made'],
                ['5',  'Origin Country',          'mandatory',   'IN',                            '2-char ISO — used for reciprocity'],
                ['6',  'Expense Category Group',  'mandatory',   'Accommodation, Automotive Fuel','Expense type in words'],
                ['7',  'Expense Amount',          'conditional', '150.00, 25.78',                 'Amount in spend currency. Mandatory if field 8 present'],
                ['8',  'Spend Currency',          'mandatory',   'EUR, GBP, SGD, NOK',            '3-char ISO currency code'],
                ['9',  'Transaction Date',        'mandatory',   'dd/mm/yyyy or YYYY-MM-DD',      'Date expense was made'],
                ['10', 'Expense Reference',       'mandatory',   'EXP-SG-001',                   'Unique ID — prevents duplicate uploads'],
              ].map(([num, name, req, example, note]) => {
                const styles: Record<string, { label: string; bg: string; color: string; rowBg: string }> = {
                  mandatory:   { label: 'Mandatory',   bg: '#fef3c7', color: '#b45309', rowBg: '#fffbeb' },
                  conditional: { label: 'Conditional', bg: '#dbeafe', color: '#1e40af', rowBg: '#eff6ff' },
                  optional:    { label: 'Optional',    bg: '#f1f5f9', color: '#64748b', rowBg: '#ffffff' },
                }
                const s = styles[req as string]
                return (
                  <tr key={num} style={{ background: s.rowBg }}>
                    <td className="mono" style={{ fontSize: 12, color: '#94a3b8', fontWeight: 600, textAlign: 'center' }}>{num}</td>
                    <td style={{ fontWeight: 600, color: '#0f172a' }}>{name}</td>
                    <td style={{ textAlign: 'center' }}>
                      <span style={{ fontSize: 11, fontWeight: 600, padding: '2px 9px', borderRadius: 5, background: s.bg, color: s.color }}>{s.label}</span>
                    </td>
                    <td><code style={{ fontSize: 11.5, background: '#f1f5f9', padding: '2px 7px', borderRadius: 4, color: '#0e7490' }}>{example}</code></td>
                    <td style={{ fontSize: 12, color: '#475569' }}>{note}</td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
        <div style={{ padding: '12px 22px', borderTop: '1px solid #e2e8f0', background: '#f8fafc', borderRadius: '0 0 14px 14px' }}>
          <InfoBox variant="blue">
            <strong>Legacy formats also accepted:</strong>&nbsp;
            <code style={{ fontSize: 11, background: '#dbeafe', padding: '1px 5px', borderRadius: 3 }}>Org Unit 2 - Code</code> + <code style={{ fontSize: 11, background: '#dbeafe', padding: '1px 5px', borderRadius: 3 }}>INR- Approved Amount</code>&nbsp;
            or&nbsp;
            <code style={{ fontSize: 11, background: '#dbeafe', padding: '1px 5px', borderRadius: 3 }}>Currency Name</code> + <code style={{ fontSize: 11, background: '#dbeafe', padding: '1px 5px', borderRadius: 3 }}>Inv Amount</code> + <code style={{ fontSize: 11, background: '#dbeafe', padding: '1px 5px', borderRadius: 3 }}>Currency Rate</code> are fully supported.
          </InfoBox>
        </div>
      </div>
    </div>
  )
}